import { useState, useEffect } from 'react';
import { useStudentTheme } from '../context/StudentThemeContext';

export default function ProgressRing({ value, max, size = 120, strokeWidth = 10, label, sublabel }) {
    const theme = useStudentTheme();
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const percentage = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    const [animatedOffset, setAnimatedOffset] = useState(circumference);
    const [animatedValue, setAnimatedValue] = useState(0);

    useEffect(() => {
        // Animate stroke from full → target
        const id = requestAnimationFrame(() => {
            setAnimatedOffset(circumference - (percentage / 100) * circumference);
        });
        // Count-up value in sync (1.2s easeOutCubic)
        const duration = 1200;
        const start = performance.now();
        const from = 0;
        const target = Number(value) || 0;
        let raf;
        const tick = (ts) => {
            const t = Math.min(1, (ts - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setAnimatedValue(from + (target - from) * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(id);
            cancelAnimationFrame(raf);
        };
    }, [value, max, circumference, percentage]);

    // Color based on progress — uses package theme primary where the default
    // would have been gold (#D4A537).
    const getColor = () => {
        if (percentage >= 100) return theme.primary;
        if (percentage >= 70) return '#38A169';
        if (percentage >= 40) return theme.primary;
        return '#4A5568';
    };

    return (
        <div className="flex flex-col items-center">
            <div className="relative" style={{ width: size, height: size }}>
                <svg
                    className="progress-ring"
                    width={size}
                    height={size}
                    style={{ transform: 'rotate(-90deg)' }}
                >
                    {/* Background circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="#2D3748"
                        strokeWidth={strokeWidth}
                    />
                    {/* Progress circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={getColor()}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={animatedOffset}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.34, 1.4, 0.64, 1), stroke 0.3s' }}
                    />
                </svg>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-white">
                        {Number.isInteger(value) ? Math.round(animatedValue) : animatedValue.toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">/ {max}</span>
                </div>
            </div>
            {label && <div className="mt-2 text-sm font-medium text-white">{label}</div>}
            {sublabel && <div className="text-xs text-gray-500">{sublabel}</div>}
        </div>
    );
}
